import { clampLayerFrame } from './clampLayerFrame';
import { isClipMirror } from './types';
import type { MediaLayer, LayerOverlayItem } from './types';
import { sanitizeMotionScene } from '@/lib/motion/sanitizeMotionScene';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Serialize editor layer state into the render input's `layers` array.
 *  Uploading / blob: layers are dropped (text never uploads); times are
 *  clamped to the clip. */
export function buildLayersRenderInput(
  layers: MediaLayer[],
  clipDuration: number,
  preview?: { invalidMotionLabel: string },
): LayerOverlayItem[] {
  return layers
    .filter((l) =>
      l.type === 'text' || l.type === 'motion' || isClipMirror(l) || (!l.uploading && !!l.url && !l.url.startsWith('blob:')),
    )
    .map((l, index): LayerOverlayItem => {
      const start = clamp(l.start, 0, clipDuration);
      const end = clamp(l.end, start, clipDuration);
      switch (l.type) {
        case 'motion': {
          const { scene } = sanitizeMotionScene(l.scene);
          if (!scene && !preview) throw new Error('invalid_motion_scene');
          const visibleScene = scene ?? { version: 1 as const, elements: [{
            id: 'invalid-motion-notice', type: 'text' as const,
            start: 0, end: Math.max(1 / 30, end - start),
            content: `${preview!.invalidMotionLabel}: ${l.name}`,
            x: 40, y: 40, size: 42, maxWidth: 1000, fill: '#ffffff',
            box: { background: '#7f1d1d', padding: 20, radius: 12 },
          }] };
          return {
            type: 'motion', scene: visibleScene, start, end,
            layer: { rect: l.rect, rotation: l.rotation, opacity: l.opacity, zIndex: index },
          };
        }
        case 'image':
          return {
            type: 'image', url: l.url, start, end,
            layer: { rect: l.rect, rotation: l.rotation, opacity: l.opacity, zIndex: index },
          };
        case 'video':
          if (isClipMirror(l)) {
            return {
              type: 'clip', start, end,
              layer: {
                rect: l.rect, rotation: l.rotation, opacity: l.opacity, zIndex: index,
                volume: l.volume,
                ...(l.frame ? { frame: clampLayerFrame(l.frame) } : {}),
              },
            };
          }
          return {
            type: 'video', url: l.url, start, end,
            layer: {
              rect: l.rect, rotation: l.rotation, opacity: l.opacity, zIndex: index,
              volume: l.volume, speed: l.speed, trimStart: l.trimStart,
              ...(l.frame ? { frame: clampLayerFrame(l.frame) } : {}),
            },
          };
        case 'audio':
          return {
            type: 'audio', url: l.url, start, end,
            layer: {
              opacity: l.opacity, zIndex: index, volume: l.volume,
              speed: l.speed, trimStart: l.trimStart,
            },
          };
        case 'text':
          return {
            type: 'text', text: l.text, style: l.style, start, end,
            layer: { rect: l.rect, rotation: l.rotation, opacity: l.opacity, zIndex: index },
          };
      }
    })
    // Zero-length windows (end === start, e.g. a layer whose clamped start
    // lands at or past clipDuration) crash the Remotion export compositions,
    // which compute durationInFrames with no floor. Preview floors at 1
    // frame so this only ever bites at export time — drop them here instead.
    .filter((item) => item.end - item.start > 0);
}
